import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { DrawerLink } from '../../app/DrawerLink';
import { Link } from 'react-router-dom';

import { Card } from '../../components/ui/Card';
import { FavoriteButton } from '../../components/ui/FavoriteButton';
import { AddToPlaylist } from '../library/AddToPlaylist';
import { SkeletonCard } from '../../components/ui/Loader';
import { usePlayer } from '../../stores/player';
import { useSession } from '../../stores/session';
import { Artwork } from './Artwork';
import { dashboard, duration, type DashboardTab, type DashboardTrack } from './api';
import { ArtistHoverCard } from '../profile/ArtistHoverCard';
import { Verified } from '../../components/ui/Verified';

/**
 * The tabs, in the order they are offered.
 *
 * "Everything" first because it is the one that always has something in it.
 * "First uploads" second because it is the tab that justifies the page - it is
 * the only view on this site where a debut is not buried under the back
 * catalogue of somebody with four hundred followers.
 *
 * The design mockup has a fourth, "Heard in rooms". Rooms are Phase 7 and there
 * is no `stations` table, so it is not here: a tab that is permanently empty
 * teaches people the site is broken, and one filled with plausible fiction is
 * worse.
 */
const TABS: { id: DashboardTab; label: string }[] = [
  { id: 'all', label: 'Everything' },
  { id: 'fresh', label: 'First uploads' },
  { id: 'following', label: 'From your follows' },
];

/**
 * "New music today" - a grid of cards, one tab at a time.
 *
 * ## Why the tab is component state and not the URL
 *
 * The charts page puts its window and genre in the query string, because "look
 * at this week's house chart" is a link somebody sends to a friend. This is not
 * that: every tab here is personal to the viewer, so a shared link would show
 * the recipient somebody else's follows. Local state, and no URL to mislead.
 */
export function NewMusicToday() {
  const me = useSession((state) => state.user);
  const [tab, setTab] = useState<DashboardTab>('all');

  /*
   * The follows tab is not offered to a stranger.
   *
   * Unlike the follow buttons, which are shown and route to sign-in, this one
   * is removed: a tab is a place, not an action, and a place that turns out to
   * be a sign-in wall is a worse experience than a shorter set of tabs. The
   * server refuses the request too, so a client that offered it anyway would
   * get a 401 rather than a misleading empty list.
   */
  const tabs = me ? TABS : TABS.filter((option) => option.id !== 'following');

  const { data, isPending } = useQuery({
    queryKey: ['dashboard', 'tracks', tab],
    queryFn: () => dashboard.tracks(tab),
    /*
     * Each tab keeps its last result while the next loads, so switching does not
     * collapse the section to a spinner and shove the rest of the page upward.
     */
    placeholderData: (previous) => previous,
  });

  const items = data?.items ?? [];

  // Everything playable on this tab, so pressing play on the fourth card queues
  // the rest of the row behind it rather than stopping after one track.
  const queue = items.filter((track) => track.status === 'ready');

  return (
    <section aria-labelledby="new-music-heading">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 id="new-music-heading" className="text-2xl font-semibold tracking-tight text-ink">
          New music today
        </h2>

        <div role="tablist" aria-label="Which new music" className="flex flex-wrap gap-1.5">
          {tabs.map((option) => (
            <button
              key={option.id}
              role="tab"
              type="button"
              aria-selected={tab === option.id}
              data-dashboard-tab={option.id}
              onClick={() => setTab(option.id)}
              className={`meta-caps rounded-[var(--radius-pill)] border px-3.5 py-2 transition-colors ${
                tab === option.id
                  ? 'border-transparent bg-accent text-accent-ink'
                  : 'border-line text-soft hover:border-line-strong hover:text-ink'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {isPending ? (
        <div
          className="mt-5 grid grid-cols-2 gap-x-4 gap-y-7 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6"
          aria-busy="true"
        >
          {/* Same grid as the real one, so pressing a tab does not resize the
              section under the cursor while the next set loads. */}
          {Array.from({ length: 6 }, (_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <Empty tab={tab} followingCount={data?.following_count ?? null} />
      ) : (
        <ul
          className="mt-5 grid grid-cols-2 gap-x-4 gap-y-7 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6"
          data-dashboard-tracks
        >
          {items.map((track) => (
            <li key={track.id} data-track-id={track.id}>
              <TrackCard track={track} queue={queue} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * One card: cover, title, artist, and the line saying why it is here.
 *
 * The whole tile is the play control rather than a small round button in a
 * corner. At this size the corner button is a 40px target inside a 200px card,
 * and everybody tries to click the artwork first anyway.
 */
function TrackCard({ track, queue }: { track: DashboardTrack; queue: DashboardTrack[] }) {
  const play = usePlayer((state) => state.play);
  const current = usePlayer((state) => state.track);
  const playing = usePlayer((state) => state.playing);
  const toggle = usePlayer((state) => state.toggle);

  const isCurrent = current?.id === track.id;
  const isPlaying = isCurrent && playing;

  return (
    <div className="group flex flex-col gap-2.5">
      <div className="relative">
        <button
          type="button"
          aria-label={isPlaying ? `Pause ${track.title}` : `Play ${track.title}`}
          onClick={() => (isCurrent ? toggle() : void play(track, queue, 'dashboard'))}
          className={[
            'relative block aspect-square w-full overflow-hidden rounded-[var(--radius-card)]',
            'border transition-colors',
            isCurrent ? 'border-accent' : 'border-line group-hover:border-line-strong',
          ].join(' ')}
        >
          <Artwork track={track} className="h-full w-full" />

          {/* Dark scrim under the glyph so a white icon survives light artwork.
              Always painted for the current track, so the page shows what is
              playing without needing a hover that a touch device cannot do.

              `veil` rather than `void`: the page colour is themed and is nearly
              white in the light theme, which made this scrim invisible and the
              glyph on it along with it. */}
          <span
            aria-hidden
            className={`absolute inset-0 flex items-center justify-center bg-veil transition-opacity ${
              isPlaying ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
            }`}
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-[var(--radius-pill)] bg-accent text-accent-ink">
              {isPlaying ? <PauseGlyph /> : <PlayGlyph />}
            </span>
          </span>

          <span className="absolute bottom-2 right-2 rounded-[var(--radius-chip)] bg-veil-strong px-1.5 py-0.5 font-mono text-[11px] text-text-2">
            {duration(track.duration_ms)}
          </span>
        </button>

        {/* Outside the play button: a button inside a button is invalid, and
            nesting them means every save also starts playback. */}
        <div className="absolute left-2 top-2 flex items-center gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
          <FavoriteButton
            trackId={track.id}
            favorited={track.favorited}
            likeCount={track.like_count}
            size="sm"
            className="rounded-[var(--radius-pill)] bg-veil-strong p-1.5"
          />

          <AddToPlaylist
            trackId={track.id}
            title={track.title}
            size="sm"
            className="bg-veil-strong"
          />
        </div>
      </div>

      <div className="flex min-w-0 flex-col gap-0.5">
        <DrawerLink
          to={`/tracks/${track.id}`}
          className="truncate text-[15px] font-medium text-ink hover:underline"
          title={track.title}
        >
          {track.title}
        </DrawerLink>

        <ArtistHoverCard username={track.owner.username} className="min-w-0">
          <Link
            to={`/${track.owner.username}`}
            className="block truncate font-mono text-xs text-faint hover:text-soft"
          >
            {track.owner.display_name}
            {track.owner?.verified && <Verified />}
          </Link>
        </ArtistHoverCard>

        {/* The reason the card is on the page. Absent when there is nothing true
            to say - see DashboardPresenter for why a filler line is worse. */}
        {track.tag && (
          <span className={`truncate font-mono text-xs ${TAG_TONES[track.tag.tone]}`} title={track.tag.label}>
            {track.tag.label}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * The design's three signal colours, kept as a lookup rather than interpolated
 * into a class name - Tailwind cannot see a class it never reads as a literal,
 * and a dynamically built one is silently absent from the stylesheet.
 */
const TAG_TONES: Record<string, string> = {
  fresh: 'text-fresh',
  follows: 'text-accent',
  rising: 'text-hot-text',
};

/**
 * Nothing to show.
 *
 * The "from your follows" tab has two ways to be empty and they want opposite
 * responses: somebody following nobody needs to be sent to find people,
 * somebody following twelve quiet accounts needs to be told it is quiet.
 * Conflating them - the usual "no results" - gives the wrong advice half the
 * time.
 */
function Empty({ tab, followingCount }: { tab: DashboardTab; followingCount: number | null }) {
  if (tab === 'following' && followingCount === 0) {
    return (
      <Card className="mt-5 p-6">
        <p className="text-sm text-soft">
          You are not following anyone yet. Find somebody in{' '}
          <Link to="/discover" className="text-accent hover:underline">
            the charts
          </Link>{' '}
          or from the artists below, and their releases will land here.
        </p>
      </Card>
    );
  }

  return (
    <Card className="mt-5 p-6">
      <p className="text-sm text-soft">
        {tab === 'following'
          ? 'Nobody you follow has released anything recently.'
          : tab === 'fresh'
            ? 'No first uploads yet. When somebody releases their first track, it shows up here.'
            : 'Nothing has been released yet.'}
      </p>
    </Card>
  );
}

function PlayGlyph() {
  return (
    <svg viewBox="0 0 20 20" className="h-5 w-5 translate-x-[1px]" fill="currentColor" aria-hidden="true">
      <path d="M6 4.5v11l10-5.5z" />
    </svg>
  );
}

function PauseGlyph() {
  return (
    <svg viewBox="0 0 20 20" className="h-5 w-5" fill="currentColor" aria-hidden="true">
      <rect x="5.5" y="4.5" width="3.5" height="11" rx="1" />
      <rect x="11" y="4.5" width="3.5" height="11" rx="1" />
    </svg>
  );
}
